import { Component, Emit, Prop, Vue } from 'vue-property-decorator'
import { AppliedBillableConceptResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { store } from '@/store/store'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { Nullable, StrictNullable } from '@/shared/types/app.types'
import {
    InvoiceReceiptResponse,
    InvoiceReceiptStatus
} from '@/api/services/billing/invoice-receipt/billing-invoice-receipt.dto'
import { billingInvoiceReceiptService } from '@/api/services/billing/invoice-receipt/billing-invoice-receipt.service'
import { FileUtils } from '@/shared/util/file-utils'
import { ModalUtils } from '@/shared/util/modal-utils'
import { BillingOperationWithConceptsGuard } from '@/modules/billing-module/shared/guards/billing-operation-with-concepts-guard'

@Component
export default class BillingModalInvoiceReceiptActionsGrid extends Vue {
    @Prop({ type: Object }) readonly invoiceReceipt!: Nullable<InvoiceReceiptResponse>
    @Prop({ type: Array, default: () => [] }) readonly appliedBillableConcepts!: AppliedBillableConceptResponse[]

    @Emit()
    added(): void {
        // emits an event
    }

    @Emit()
    refresh(invoiceReceiptId: number): number {
        return invoiceReceiptId
    }

    /// COMPUTED ///

    private get _invoiceReceiptGenerated(): boolean {
        return this.invoiceReceipt?.status.code === InvoiceReceiptStatus.GENERATED
    }
    private get _invoiceReceiptVerified(): boolean {
        return this.invoiceReceipt?.status.code === InvoiceReceiptStatus.VERIFIED
    }

    get visibleOpenInvoiceReceiptReport(): boolean {
        return this._invoiceReceiptGenerated || this._invoiceReceiptVerified
    }
    get visibleDeleteConcepts(): boolean {
        return this._invoiceReceiptGenerated
    }

    private static _canRemoveConcepts(concepts: AppliedBillableConceptResponse[]): boolean {
        return !!concepts.length
    }
    get canRemoveSelectedConcepts(): boolean {
        return BillingModalInvoiceReceiptActionsGrid._canRemoveConcepts(this.appliedBillableConcepts)
    }

    get canOpenInvoiceReceiptReport(): boolean {
        return !!this.invoiceReceipt
    }

    /// SERVICES CALLS ///

    private async _deleteInvoiceReceiptSelectedConcepts(conceptsIds: number[]): Promise<void> {
        if (this.invoiceReceipt && conceptsIds.length) {
            const { id } = this.invoiceReceipt
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoiceReceiptService.removeConcepts(
                        store.state.labId,
                        id,
                        conceptsIds))
            if (response !== null) {
                const choice = conceptsIds.length > 1 ? 2 : 1
                ToastUtils.showToastSuccess(this.$tc('accounting.concept.deleted', choice))
                this.refresh(id) // emits an event
            }
        }
    }
    private async _deleteInvoiceReceiptAllConcepts(): Promise<void> {
        if (this.invoiceReceipt) {
            const { id } = this.invoiceReceipt
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoiceReceiptService.removeAllConcepts(
                        store.state.labId,
                        id))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('accounting.concept.deleted', 2))
                this.refresh(id) // emits an event
            }
        }
    }

    private _downloadInvoiceReceiptReport(invoiceReceiptId: number): Promise<StrictNullable<Blob>> {
        return PromiseUtils.toCallSecure(
            billingInvoiceReceiptService.getReport(store.getters.labId, invoiceReceiptId, false),
            error => ModalUtils.showErrorModalFromErrorResponse(error, this.$tc('print.modals.errorReport')))
    }

    /// EVENT HANDLERS ///

    async onConceptsClean(): Promise<void> {
        if (this.invoiceReceipt) {
            const { appliedBillableConceptCount } = this.invoiceReceipt
            await BillingOperationWithConceptsGuard.run(
                appliedBillableConceptCount,
                this._deleteInvoiceReceiptAllConcepts
            )
        }
    }
    async onConceptsDeleted(
        conceptIds = this.appliedBillableConcepts.map(abc => abc.id)
    ): Promise<void> {
        await BillingOperationWithConceptsGuard.run(
            conceptIds.length,
            () => this._deleteInvoiceReceiptSelectedConcepts(conceptIds)
        )
    }

    async onOpenInvoiceReceiptReport(): Promise<void> {
        if (this.invoiceReceipt) {
            const blob = await this._downloadInvoiceReceiptReport(this.invoiceReceipt.id)
            if (blob !== null) {
                FileUtils.openFile({ blob })
            }
        }
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        const menuItems: MenuItemDef[] = []
        if (params.node) {
            const selectedNode: AppliedBillableConceptResponse = params.node.data
            if (BillingModalInvoiceReceiptActionsGrid._canRemoveConcepts([selectedNode])) {
                menuItems.push({
                    name: this.$tc('accounting.action.deleteConcept'),
                    action: () => this.onConceptsDeleted([selectedNode.id])
                })
            }
        }
        return menuItems
    }
}
